import React, { useState } from 'react';
import { Button } from '@/app/_module/components/ui/button';
import { useToast } from '@/app/_module/hooks/use-toast';
import { cn } from '@/app/_module/lib/utils';
import { a2pService } from '@/app/_module/services/a2pService';
import { controlBorder, focusRing } from './primitives';

/** Telnyx sole proprietors confirm with a texted PIN instead of replying YES. */
export function OtpForm({ onVerified }: { onVerified: () => void }) {
  const { toast } = useToast();
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length !== 6) return;
    setBusy(true);
    try {
      const r = await a2pService.verifyOtp(pin);
      toast({ title: 'Code accepted', description: r?.message || 'Carrier review has started.' });
      setPin('');
      onVerified();
    } catch (error: any) {
      toast({ title: 'Code not accepted', description: error?.message || 'Check the code and try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const r = await a2pService.resendOtp();
      toast({ title: 'New code sent', description: r?.message || 'Check your business phone.' });
    } catch (error: any) {
      toast({ title: "Couldn't send a new code", description: error?.message || 'Try again in a minute.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <label htmlFor="a2p-otp" className="text-sm font-medium text-neutral-900">
        6-digit code
      </label>
      <div className="mt-1.5 flex flex-col gap-2.5 sm:flex-row sm:items-center">
        <input
          id="a2p-otp"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          className={cn(
            'h-11 w-full rounded-md border bg-white px-3 text-center font-mono text-lg tabular-nums tracking-[0.35em] text-neutral-900 sm:w-44',
            controlBorder,
            focusRing,
          )}
        />
        <Button type="submit" size="lg" disabled={pin.length !== 6 || busy} className={cn('px-6', focusRing)}>
          {busy ? 'Checking…' : 'Confirm code'}
        </Button>
        <Button type="button" variant="ghost" onClick={resend} disabled={busy} className={cn('h-11', focusRing)}>
          Send a new code
        </Button>
      </div>
    </form>
  );
}
